import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workflowApi, type WorkflowExecution, type WorkflowStep } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { io, type Socket } from 'socket.io-client';
import {
  Zap, ArrowLeft, Circle, CheckCircle2,
  Clock, RefreshCw, XCircle, ArrowRight
} from 'lucide-react';
import { toast } from '@/components/ui/toast';
import styles from './_auth.workflows.executions.module.css';

export const Route = createFileRoute('/_auth/workflows/executions/$executionId')({
  component: ExecutionDetailPage,
});

const WS_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL?.replace(/\/v1$/, '') ?? 'http://localhost:3001';

export default function ExecutionDetailPage() {
  const { executionId } = Route.useParams();
  const { platformUser, idToken } = useAuth();
  const qc = useQueryClient();

  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0);
  const [callbackDataText, setCallbackDataText] = useState('{\n  "approved": true\n}');
  const [socketConnected, setSocketConnected] = useState(false);

  const { data: execution, isLoading } = useQuery({
    queryKey: ['workflow-execution', executionId],
    queryFn: () => workflowApi.getExecution(executionId),
  });

  // Connect websocket for live updates
  useEffect(() => {
    if (!platformUser || !idToken) return;

    let socket: Socket | null = null;
    let active = true;

    async function initSocket() {
      if (!active) return;
      socket = io(`${WS_URL}/admin`, {
        auth: { token: idToken },
        query: { tenantId: platformUser?.tenant.id },
        transports: ['websocket'],
        reconnectionDelay: 3000,
        reconnectionAttempts: 10,
      });

      socket.on('connect', () => {
        setSocketConnected(true);
      });

      socket.on('disconnect', () => {
        setSocketConnected(false);
      });

      socket.on('workflow:updated', (data: { executionId: string; execution: WorkflowExecution }) => {
        if (data.executionId === executionId) {
          // Push live update into TanStack Query cache
          qc.setQueryData(['workflow-execution', executionId], data.execution);
        }
      });
    }

    void initSocket();

    return () => {
      active = false;
      socket?.disconnect();
    };
  }, [platformUser?.tenant.id, idToken, executionId]);

  const cancelMutation = useMutation({
    mutationFn: () => workflowApi.cancelExecution(executionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-execution', executionId] });
    },
  });

  const callbackMutation = useMutation({
    mutationFn: (data: unknown) => workflowApi.resumeExecution(executionId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow-execution', executionId] });
    },
  });

  const handleCallbackSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const parsed = JSON.parse(callbackDataText);
      callbackMutation.mutate(parsed);
    } catch {
      toast.error('Callback payload must be valid JSON');
    }
  };

  if (isLoading || !execution) {
    return <div className={styles.page}><p>Loading execution details...</p></div>;
  }

  const steps = (execution.definition?.steps || []) as WorkflowStep[];
  const activeStep = steps[selectedStepIndex];
  const activeStepOutput = execution.state?.[`step_${selectedStepIndex}_output`];
  const activeStepCallback = execution.state?.[`step_${selectedStepIndex}_callback`];

  const getStepStatus = (index: number) => {
    if (index < execution.currentStepIndex) return 'COMPLETED';
    if (index === execution.currentStepIndex) {
      if (execution.status === 'FAILED') return 'FAILED';
      if (execution.status === 'WAITING_CALLBACK') return 'WAITING_CALLBACK';
      if (execution.status === 'WAITING_APPROVAL') return 'WAITING_APPROVAL';
      if (execution.status === 'COMPLETED') return 'COMPLETED';
      if (execution.status === 'CANCELLED') return 'CANCELLED';
      return 'RUNNING';
    }
    return 'PENDING';
  };

  return (
    <div className={styles.page}>
      {/* Header section */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <Link
            to="/workflows/$workflowId"
            params={{ workflowId: execution.definitionId }}
            className={styles.backLink}
          >
            <ArrowLeft size={14} /> Back to Workflow definition
          </Link>
          <h1 className={styles.title}>
            <Zap size={24} className="text-primary" /> Execution Run
          </h1>
          <p className={styles.subtitle} style={{ fontFamily: 'var(--font-mono)' }}>
            ID: {execution.id}
          </p>
          <div className={styles.statusRow}>
            <span>Status:
              <span className={`${styles.statusPill} ${styles[`status${execution.status}`]}`} style={{ marginLeft: '0.4rem' }}>
                {execution.status}
              </span>
            </span>
            <span>Created: {new Date(execution.createdAt).toLocaleString()}</span>
            {socketConnected ? (
              <span style={{ color: 'var(--color-success)' }}>● Real-time connected</span>
            ) : (
              <span style={{ color: 'var(--color-muted)' }}>○ Reconnecting...</span>
            )}
          </div>
        </div>

        {/* Header actions */}
        {(execution.status === 'RUNNING' || execution.status === 'PENDING') && (
          <button
            className={styles.cancelBtn}
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
          >
            Cancel Execution
          </button>
        )}
      </div>

      {execution.error && (
        <div className={styles.errorBox} style={{ marginBottom: '1.5rem' }}>
          <span className={styles.errorTitle}>Execution error:</span>
          <span>{execution.error}</span>
        </div>
      )}

      {/* Main split grid */}
      <div className={styles.layout}>
        {/* Left Side: Step cards */}
        <div className={styles.timeline}>
          <h3 className={styles.timelineTitle}>Execution Steps</h3>
          {steps.length === 0 ? (
            <p className={styles.subtitle}>No steps configured in this definition version.</p>
          ) : (
            steps.map((step, idx) => {
              const status = getStepStatus(idx);
              const isSelected = selectedStepIndex === idx;

              return (
                <div
                  key={step.id}
                  className={`${styles.stepCard} ${isSelected ? styles.stepCardActive : ''}`}
                  onClick={() => setSelectedStepIndex(idx)}
                >
                  <div className={styles.stepInfo}>
                    <div className={styles.stepIcon}>{idx + 1}</div>
                    <div className={styles.stepDetails}>
                      <span className={styles.stepName}>{step.name}</span>
                      <div className={styles.stepMeta}>
                        <span className={styles.typeBadge}>{step.type}</span>
                        {step.id && <span style={{ fontFamily: 'var(--font-mono)' }}>id: {step.id}</span>}
                      </div>
                    </div>
                  </div>

                  <div className={styles.stepStatus}>
                    {status === 'COMPLETED' && <CheckCircle2 size={18} style={{ color: 'var(--color-success)' }} />}
                    {status === 'FAILED' && <XCircle size={18} style={{ color: 'var(--color-error)' }} />}
                    {status === 'RUNNING' && <RefreshCw size={18} className="animate-spin" style={{ color: 'var(--color-primary)' }} />}
                    {(status === 'WAITING_CALLBACK' || status === 'WAITING_APPROVAL') && (
                      <Clock size={18} style={{ color: 'var(--color-warning)' }} />
                    )}
                    {status === 'PENDING' && <Circle size={18} style={{ color: 'var(--color-muted)' }} />}
                    {status === 'CANCELLED' && <XCircle size={18} style={{ color: 'var(--color-muted)' }} />}
                    <ArrowRight size={14} style={{ opacity: isSelected ? 0.8 : 0.2 }} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right Side: Step Details details panel */}
        <div className={styles.detailPane}>
          {activeStep ? (
            <>
              {/* Configuration parameters */}
              <div className={styles.detailCard}>
                <h4>Step Definition Configuration</h4>
                <div className={styles.stepMeta} style={{ marginBottom: '0.75rem' }}>
                  <span className={styles.typeBadge}>{activeStep.type}</span>
                  <span>ID: {activeStep.id}</span>
                </div>
                <pre className={styles.jsonBlock}>
                  {JSON.stringify(activeStep, null, 2)}
                </pre>
              </div>

              {/* Execution Outputs */}
              <div className={styles.detailCard}>
                <h4>Execution Output</h4>
                {activeStepOutput !== undefined ? (
                  <pre className={styles.jsonBlock}>
                    {JSON.stringify(activeStepOutput, null, 2)}
                  </pre>
                ) : (
                  <p className={styles.subtitle} style={{ fontSize: '0.8rem' }}>
                    This step output is not available yet (either not run or currently running).
                  </p>
                )}
              </div>

              {/* Callback Data received */}
              {activeStepCallback !== undefined && (
                <div className={styles.detailCard}>
                  <h4>Received Callback Payload</h4>
                  <pre className={styles.jsonBlock}>
                    {JSON.stringify(activeStepCallback, null, 2)}
                  </pre>
                </div>
              )}

              {/* Callback trigger box */}
              {execution.status === 'WAITING_CALLBACK' && execution.currentStepIndex === selectedStepIndex && (
                <div className={styles.detailCard}>
                  <h4>Trigger Webhook Mock Callback</h4>
                  <p className={styles.subtitle} style={{ marginBottom: '1rem', fontSize: '0.8rem' }}>
                    Provide callback JSON data payload to resume this execution step.
                  </p>
                  <form onSubmit={handleCallbackSubmit} className={styles.callbackForm}>
                    <textarea
                      className={styles.callbackInput}
                      value={callbackDataText}
                      onChange={(e) => setCallbackDataText(e.target.value)}
                    />
                    <button
                      type="submit"
                      disabled={callbackMutation.isPending}
                      className={styles.callbackBtn}
                    >
                      {callbackMutation.isPending ? 'Sending...' : 'Send Callback Resume'}
                    </button>
                  </form>
                </div>
              )}
            </>
          ) : (
            <div className={styles.detailCard}>
              <h4>No step selected</h4>
              <p className={styles.subtitle}>Select a step on the left to see inputs/outputs.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
